import { TextField } from '@client/new/components/basic';
import {
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from '@mui/material';
import { useURLBuilder } from './useURLBuilder';

import { useEffect, useState } from 'react';
import { SalesType } from '@client/new/common/types';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';

import React from 'react';
import {
  InfoGeneralProps,
  IntegrationInfoTip,
  capitalize,
} from './info-sections';
import { SuccessModal } from './SuccessModal';
import { Casted, F1, Option, TypedOmit } from '../../../helpers/types';
import { ErrorMessage, Label } from '../../../components/Label';
import {
  SourceType,
  URLForm,
  UseURLBuilderState,
  urlSourceOptions,
} from './useURLBuilderState';
import { Button } from '../../../../../design-system/components/button/button';
import { Typography } from '../../../../../design-system/components/typography/typography';
import { mixTrack } from '@services/mixpanel';
import { LiteUser } from '@client/new/common/LiteUser.mapper';
import { OTPProductSelect } from './OtpProductSelect';
import { ProductOption, useOTPProducts } from './useOtpProducts';
import { isEmpty } from 'lodash';

const FieldWithInfo = (p: React.PropsWithChildren<{ info: string }>) => (
  <div className="flex flex-col gap-1">
    {p.children}
    <Typography
      variant="body"
      size="small"
      color="N-60"
      style={{ paddingLeft: '1em' }}
    >
      {p.info}
    </Typography>
  </div>
);

type ProductSelectProps = {
  value: SalesType | undefined;
  onChange: F1<{ target: { value: SalesType } }>;
  setOtp: F1<ProductOption>;
  otp: ProductOption | undefined;
  error?: string;
  isFormDirty: boolean;
  hasOTP?: boolean;
  otps: ProductOption[];
};

const ProductSelect = ({ otp, setOtp, ...p }: ProductSelectProps) => {
  useEffect(() => {
    if (!p.hasOTP) p.onChange({ target: { value: 'subscription' } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.hasOTP]);

  if (!p.hasOTP) return null;
  const options: Array<Option<SalesType>> = [
    { value: 'subscription', label: 'Subscription' },
    { value: 'otp', label: 'One Time Purchase' },
  ];
  const otpError =
    p.isFormDirty && otp === undefined ? 'Please select product' : '';
  return (
    <>
      <div className="flex flex-col gap-4">
        <Label error={p.error} content={labels.productType} />
        <div style={{ marginTop: '-8px', marginLeft: '1em' }}>
          <RadioGroup
            row
            value={p.value}
            onChange={(e) =>
              p.onChange({ target: { value: e.target.value as SalesType } })
            }
            className="mt-0"
          >
            {options.map((o) => (
              <FormControlLabel
                key={o.value}
                value={o.value}
                control={<Radio />}
                label={o.label}
              />
            ))}
          </RadioGroup>
        </div>
        <div style={{ marginTop: '-8px' }}>
          <ErrorMessage content={p.error} />
        </div>
      </div>
      {p.value === 'otp' ? (
        <div className="">
          <OTPProductSelect
            label="Product"
            placeholder="Select"
            value={otp}
            onChange={(v) => (v ? setOtp(v) : null)}
            error={otpError}
            otps={p.otps}
          />
        </div>
      ) : null}
    </>
  );
};

export type CampaignDetailsProps = { creator: LiteUser } & InfoGeneralProps &
  TypedOmit<UseURLBuilderState, 'setStarted' | 'started'>;

const labels: Casted<URLForm, string> = {
  name: 'Campaign',
  source: 'Source',
  productType: 'Choose a product to advertise',
};

export const CampaignDetails = ({
  parse,
  data,
  otp,
  errors,
  set,
  setOtp,
  isFormDirty,
  ...p
}: CampaignDetailsProps) => {
  const otps = useOTPProducts(p.creator.id);
  const builder = useURLBuilder(p.creator.customURL);
  const [isOpen, setIsOpen] = useState(false);

  const onGenerate = () => {
    if (!parse(data)) return;
    builder.setData({ ...data, product: otp } as URLForm & {
      product: ProductOption;
    });
    setIsOpen(true);
    p.setIsFormDirty(false);

    mixTrack({
      type: 'Campaign Named',
      payload: {},
    });
    mixTrack({
      type: 'Link Generated',
      payload: {},
    });
  };

  return (
    <>
      <Typography variant="headline" size="medium">
        Campaign details
      </Typography>

      {builder.url && !isOpen ? (
        <Typography
          variant="body"
          size="small"
          color="N-80"
          style={{ marginTop: '8px' }}
        >
          To get new link, just update the campaign details 👇
        </Typography>
      ) : null}

      <>
        <FieldWithInfo info="Identifies a unique marketing campaign or source of traffic (e.g. Instagram bio, Linktree, Black Friday email campaign, etc)">
          <Label error={errors.name} content={labels.name} />
          <TextField
            name="campaign-name"
            value={data.name}
            onChange={set('name')}
            placeholder="Name your campaign"
            errorMsg={errors.name}
            required
          />
        </FieldWithInfo>

        <FieldWithInfo info="Identifies which site or application traffic is coming from (e.g. TikTok, Website, Email, etc)">
          <FormLabel className="flex flex-col gap-2">
            <Label
              id="source-label-id"
              error={errors.source}
              content={labels.source}
              style={{ marginBottom: '-5px' }}
            />
            <Select<SourceType>
              name="campaign-source"
              labelId="source-label-id"
              value={data.source}
              onChange={(v) => {
                set('source')(v as any);
                mixTrack({
                  type: 'Source Selected',
                  payload: {
                    'Source Type': v.target.value,
                  },
                });
              }}
              error={errors.source !== undefined}
              renderValue={(v) =>
                v ? (
                  capitalize(v as string)
                ) : (
                  <span style={{ color: '#667085' }}>
                    Select campaign source
                  </span>
                )
              }
              displayEmpty
              required
            >
              {urlSourceOptions.map((value) => (
                <MenuItem value={value} key={value}>
                  {capitalize(value)}
                </MenuItem>
              ))}
            </Select>
          </FormLabel>
          <ErrorMessage content={errors.source} />
        </FieldWithInfo>

        <IntegrationInfoTip
          source={data.source}
          creator={p.creator}
          onNavigateToIntegration={p.onNavigateToIntegration}
        />

        <AsyncRenderer
          data={otps}
          renderer={(otps) => (
            <ProductSelect
              value={data.productType}
              error={errors.productType}
              otp={otp}
              setOtp={setOtp}
              isFormDirty={isFormDirty}
              hasOTP={!isEmpty(otps)}
              otps={otps}
              onChange={(e) => {
                set('productType')(e);

                mixTrack({
                  type: 'Product Selected',
                  payload: {
                    'Product Type': e.target.value,
                  },
                });
              }}
            />
          )}
        />
        <div className="flex flex-1 justify-end">
          <Button
            color="neutral"
            style={{
              minWidth: '120px',
              marginTop: '48px',
              alignSelf: 'flex-end',
              justifySelf: 'flex-end',
            }}
            onClick={onGenerate}
          >
            Generate Link
          </Button>
        </div>
      </>
      <SuccessModal
        isOpen={isOpen && builder.url !== ''}
        onClose={() => setIsOpen(false)}
        url={builder.url}
      />
    </>
  );
};
